// Move a doc or folder into another folder (or the root). Lists destinations the
// user can edit, excluding the item itself and its descendants (no cycles).

import { useMemo, useState } from "react";
import { Modal } from "./Modal";
import { Icon } from "./Icon";
import { moveItem, type OrganizeKind } from "../folders";
import { useToast } from "./Toast";
import type { FolderMeta } from "../types";

interface Props {
  kind: OrganizeKind;
  id: string;
  name: string;
  currentParentId: string | null;
  folders: FolderMeta[];
  emailLower: string;
  onClose: () => void;
  onMoved: () => void;
}

export function MoveDialog({
  kind,
  id,
  name,
  currentParentId,
  folders,
  emailLower,
  onClose,
  onMoved,
}: Props) {
  const [target, setTarget] = useState<string | null>(currentParentId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { success } = useToast();

  // Destinations: folders the user can edit, minus the folder being moved and
  // its subtree (which would create a cycle).
  const destinations = useMemo(() => {
    const canEdit = (f: FolderMeta) =>
      f.ownerEmail === emailLower || (f.editorMembers ?? []).includes(emailLower);
    return folders
      .filter((f) => {
        if (kind === "folder") {
          if (f.folderId === id) return false;
          if ((f.ancestors ?? []).includes(id)) return false;
        }
        return canEdit(f);
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [folders, kind, id, emailLower]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await moveItem(kind, id, target);
      success(`Moved “${name}”.`);
      onMoved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const pathLabel = (f: FolderMeta) => {
    const names = (f.ancestors ?? [])
      .map((aid) => folders.find((x) => x.folderId === aid)?.name)
      .filter(Boolean);
    return [...names, f.name].join(" / ");
  };

  return (
    <Modal
      title={`Move “${name}”`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={busy || target === currentParentId}
          >
            Move here
          </button>
        </>
      }
    >
      <ul className="picker-list">
        <li>
          <button
            type="button"
            className={`picker-row${target === null ? " is-selected" : ""}`}
            onClick={() => setTarget(null)}
          >
            <Icon name="file" size={16} />
            <span>All documents (root)</span>
            {target === null && <Icon name="check" size={16} className="picker-check" />}
          </button>
        </li>
        {destinations.map((f) => (
          <li key={f.folderId}>
            <button
              type="button"
              className={`picker-row${target === f.folderId ? " is-selected" : ""}`}
              onClick={() => setTarget(f.folderId)}
            >
              <Icon name="folder" size={16} />
              <span>{pathLabel(f)}</span>
              {target === f.folderId && (
                <Icon name="check" size={16} className="picker-check" />
              )}
            </button>
          </li>
        ))}
      </ul>

      {error && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
